import React, { useRef, useEffect } from "react";

/**
 * Pharmaceutical/biotech themed animated canvas background.
 * Renders floating molecules, DNA helixes, and neural-network-style connections.
 */
const BioBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let w, h;

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = document.documentElement.scrollHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Refresh canvas height periodically as content loads
    const resizeInterval = setInterval(() => {
      const newH = document.documentElement.scrollHeight;
      if (Math.abs(newH - h) > 50) {
        h = canvas.height = newH;
        w = canvas.width = window.innerWidth;
      }
    }, 2000);

    // === MOLECULES ===
    const molecules = [];
    const MOL_COUNT = 18;
    for (let i = 0; i < MOL_COUNT; i++) {
      molecules.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 3 + Math.random() * 5,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.2,
        opacity: 0.08 + Math.random() * 0.12,
        color: Math.random() > 0.5 ? "229,161,0" : "13,94,168", // accent gold or medical blue
        // Satellite atoms
        satellites: Math.floor(1 + Math.random() * 3),
        satDist: 12 + Math.random() * 14,
        satAngle: Math.random() * Math.PI * 2,
        satSpeed: (0.003 + Math.random() * 0.008) * (Math.random() > 0.5 ? 1 : -1),
      });
    }

    // === DNA HELIXES ===
    const helixes = [];
    const HELIX_COUNT = 3;
    for (let i = 0; i < HELIX_COUNT; i++) {
      helixes.push({
        x: w * (0.15 + Math.random() * 0.7),
        yStart: Math.random() * h * 0.3,
        length: h * (0.15 + Math.random() * 0.2),
        amplitude: 20 + Math.random() * 25,
        frequency: 0.03 + Math.random() * 0.02,
        phase: Math.random() * Math.PI * 2,
        speed: 0.008 + Math.random() * 0.006,
        opacity: 0.04 + Math.random() * 0.04,
        drift: (Math.random() - 0.5) * 0.15,
      });
    }

    // === FLOATING HEXAGONS (benzene rings) ===
    const hexagons = [];
    const HEX_COUNT = 8;
    for (let i = 0; i < HEX_COUNT; i++) {
      hexagons.push({
        x: Math.random() * w,
        y: Math.random() * h,
        size: 10 + Math.random() * 18,
        rotation: Math.random() * Math.PI,
        rotSpeed: (Math.random() - 0.5) * 0.005,
        vy: -0.1 - Math.random() * 0.2,
        vx: (Math.random() - 0.5) * 0.15,
        opacity: 0.04 + Math.random() * 0.06,
      });
    }

    // === NEURAL CONNECTIONS (between nearby molecules) ===
    const CONNECTION_DIST = 200;

    // === FLOATING CROSS MARKERS (+) like medical symbols ===
    const crosses = [];
    const CROSS_COUNT = 6;
    for (let i = 0; i < CROSS_COUNT; i++) {
      crosses.push({
        x: Math.random() * w,
        y: Math.random() * h,
        size: 6 + Math.random() * 8,
        opacity: 0.05 + Math.random() * 0.06,
        vy: -0.05 - Math.random() * 0.1,
        vx: (Math.random() - 0.5) * 0.1,
        rotation: Math.random() * Math.PI * 0.25,
        rotSpeed: (Math.random() - 0.5) * 0.003,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      // --- Draw DNA helixes ---
      helixes.forEach((helix) => {
        helix.phase += helix.speed;
        helix.x += helix.drift;
        if (helix.x < -50) helix.x = w + 50;
        if (helix.x > w + 50) helix.x = -50;

        const steps = Math.floor(helix.length / 4);
        for (let s = 0; s < steps; s++) {
          const t = s / steps;
          const y = helix.yStart + t * helix.length;
          const x1 = helix.x + Math.sin(t * helix.length * helix.frequency + helix.phase) * helix.amplitude;
          const x2 = helix.x + Math.sin(t * helix.length * helix.frequency + helix.phase + Math.PI) * helix.amplitude;

          // Strand dots
          ctx.beginPath();
          ctx.arc(x1, y, 1.8, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(229,161,0,${helix.opacity})`;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(x2, y, 1.8, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(13,94,168,${helix.opacity})`;
          ctx.fill();

          // Base pair connections (every few steps)
          if (s % 5 === 0) {
            ctx.beginPath();
            ctx.moveTo(x1, y);
            ctx.lineTo(x2, y);
            ctx.strokeStyle = `rgba(229,161,0,${helix.opacity * 0.5})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      });

      // --- Draw hexagons (benzene rings) ---
      hexagons.forEach((hex) => {
        hex.y += hex.vy;
        hex.x += hex.vx;
        hex.rotation += hex.rotSpeed;
        if (hex.y < -40) { hex.y = h + 40; hex.x = Math.random() * w; }
        if (hex.x < -40) hex.x = w + 40;
        if (hex.x > w + 40) hex.x = -40;

        ctx.save();
        ctx.translate(hex.x, hex.y);
        ctx.rotate(hex.rotation);
        ctx.beginPath();
        for (let v = 0; v < 6; v++) {
          const angle = (Math.PI / 3) * v - Math.PI / 6;
          const px = Math.cos(angle) * hex.size;
          const py = Math.sin(angle) * hex.size;
          v === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(229,161,0,${hex.opacity})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        // Inner circle (benzene)
        ctx.beginPath();
        ctx.arc(0, 0, hex.size * 0.55, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(229,161,0,${hex.opacity * 0.5})`;
        ctx.lineWidth = 0.6;
        ctx.stroke();

        ctx.restore();
      });

      // --- Draw molecules ---
      molecules.forEach((mol) => {
        mol.x += mol.vx;
        mol.y += mol.vy;
        mol.satAngle += mol.satSpeed;

        // Wrap
        if (mol.x < -30) mol.x = w + 30;
        if (mol.x > w + 30) mol.x = -30;
        if (mol.y < -30) mol.y = h + 30;
        if (mol.y > h + 30) mol.y = -30;

        // Central atom
        ctx.beginPath();
        ctx.arc(mol.x, mol.y, mol.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${mol.color},${mol.opacity})`;
        ctx.fill();

        // Glow
        const grd = ctx.createRadialGradient(mol.x, mol.y, 0, mol.x, mol.y, mol.r * 3);
        grd.addColorStop(0, `rgba(${mol.color},${mol.opacity * 0.3})`);
        grd.addColorStop(1, `rgba(${mol.color},0)`);
        ctx.beginPath();
        ctx.arc(mol.x, mol.y, mol.r * 3, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();

        // Satellite atoms
        for (let s = 0; s < mol.satellites; s++) {
          const angle = mol.satAngle + (Math.PI * 2 / mol.satellites) * s;
          const sx = mol.x + Math.cos(angle) * mol.satDist;
          const sy = mol.y + Math.sin(angle) * mol.satDist;

          // Bond line
          ctx.beginPath();
          ctx.moveTo(mol.x, mol.y);
          ctx.lineTo(sx, sy);
          ctx.strokeStyle = `rgba(${mol.color},${mol.opacity * 0.4})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();

          // Satellite dot
          ctx.beginPath();
          ctx.arc(sx, sy, mol.r * 0.45, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${mol.color},${mol.opacity * 0.8})`;
          ctx.fill();
        }
      });

      // --- Neural connections ---
      for (let i = 0; i < molecules.length; i++) {
        for (let j = i + 1; j < molecules.length; j++) {
          const dx = molecules[i].x - molecules[j].x;
          const dy = molecules[i].y - molecules[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            const alpha = (1 - dist / CONNECTION_DIST) * 0.04;
            ctx.beginPath();
            ctx.moveTo(molecules[i].x, molecules[i].y);
            ctx.lineTo(molecules[j].x, molecules[j].y);
            ctx.strokeStyle = `rgba(229,161,0,${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }

      // --- Medical crosses ---
      crosses.forEach((c) => {
        c.y += c.vy;
        c.x += c.vx;
        c.rotation += c.rotSpeed;
        if (c.y < -30) { c.y = h + 30; c.x = Math.random() * w; }

        ctx.save();
        ctx.translate(c.x, c.y);
        ctx.rotate(c.rotation);
        ctx.strokeStyle = `rgba(13,94,168,${c.opacity})`;
        ctx.lineWidth = 1.5;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(0, -c.size);
        ctx.lineTo(0, c.size);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(-c.size, 0);
        ctx.lineTo(c.size, 0);
        ctx.stroke();
        ctx.restore();
      });

      animId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animId);
      clearInterval(resizeInterval);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
};

export default BioBackground;
